import React, { Component } from "react";
import { FormattedDate } from "react-intl";
import fetchUtil from "../../../../utils/fetch";
import Button from "../../../../components/form/Button";

const emptyEntry = { esmFunction: null, validFrom: "", validTo: "" };

class EsmEntriesSection extends Component {
  state = {
    entries: [],
    esmFunctions: [],
    loading: true,
    editingEntry: null,  // null = not editing, {} = new entry, {id,...} = editing existing
    editForm: { ...emptyEntry },
    saveError: null,
  };

  async componentDidMount() {
    const { auth, officialId, dispatch } = this.props;
    try {
      const [entriesRes, functionsRes] = await Promise.all([
        fetchUtil(`${auth.startUrl}/api/funkcionari/${officialId}/esm`, dispatch),
        fetchUtil("/api/ciselniky/esm-funkce?pageSize=1000&sort=name&order=ASC", dispatch),
      ]);
      const entries = await entriesRes.json();
      const functionsData = await functionsRes.json();
      this.setState({
        entries: Array.isArray(entries) ? entries : [],
        esmFunctions: functionsData.items || [],
        loading: false,
      });
    } catch (e) {
      this.setState({ loading: false });
    }
  }

  startAdd = () => {
    this.setState({ editingEntry: "new", editForm: { ...emptyEntry }, saveError: null });
  };

  startEdit = (entry) => {
    this.setState({
      editingEntry: entry.id,
      editForm: {
        esmFunction: entry.esmFunction ? entry.esmFunction.id : "",
        validFrom: entry.validFrom || "",
        validTo: entry.validTo || "",
      },
      saveError: null,
    });
  };

  cancelEdit = () => {
    this.setState({ editingEntry: null, editForm: { ...emptyEntry }, saveError: null });
  };

  onFormChange = (field, value) => {
    this.setState((prev) => ({ editForm: { ...prev.editForm, [field]: value } }));
  };

  saveEntry = async () => {
    const { auth, officialId, dispatch } = this.props;
    const { editingEntry, editForm, esmFunctions } = this.state;

    if (!editForm.esmFunction || !editForm.validFrom) {
      this.setState({ saveError: "Veřejná funkce a Datum od jsou povinné." });
      return;
    }

    const esmFunctionObj = esmFunctions.find((f) => f.id === editForm.esmFunction);
    const payload = {
      esmFunction: esmFunctionObj,
      validFrom: editForm.validFrom || null,
      validTo: editForm.validTo || null,
    };

    const isNew = editingEntry === "new";
    const url = isNew
      ? `${auth.startUrl}/api/funkcionari/${officialId}/esm`
      : `${auth.startUrl}/api/funkcionari/${officialId}/esm/${editingEntry}`;

    if (!isNew) {
      payload.id = editingEntry;
    }

    const method = isNew ? "POST" : "PUT";
    let saved;
    try {
      const response = await fetchUtil(url, dispatch, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      saved = await response.json();
    } catch (e) {
      this.setState({ saveError: "Chyba při ukládání záznamu." });
      return;
    }

    this.setState((prev) => {
      const entries = isNew
        ? [...prev.entries, saved]
        : prev.entries.map((e) => (e.id === saved.id ? saved : e));
      return { entries, editingEntry: null, editForm: { ...emptyEntry }, saveError: null };
    });
  };

  deleteEntry = async (entryId) => {
    const { auth, officialId, dispatch } = this.props;

    try {
      await fetchUtil(
        `${auth.startUrl}/api/funkcionari/${officialId}/esm/${entryId}`,
        dispatch,
        { method: "DELETE" },
      );
      this.setState((prev) => ({
        entries: prev.entries.filter((e) => e.id !== entryId),
      }));
    } catch (e) {
      // error already dispatched by fetchUtil
    }
  };

  render() {
    const { disabled } = this.props;
    const { entries, esmFunctions, loading, editingEntry, editForm, saveError } = this.state;

    if (loading) {
      return <div>Načítání ESM záznamů...</div>;
    }

    return (
      <div style={{ width: "100%" }}>
        <div className="grid-wrapper">
          <div className="grid-scroll">
            <table className="grid">
              <thead>
                <tr>
                  <th className="text"><span>Veřejná funkce</span></th>
                  <th className="text"><span>Od data</span></th>
                  <th className="text"><span>Do data</span></th>
                  {!disabled && <th className="text" />}
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id}>
                    <td className="text">{entry.esmFunction ? entry.esmFunction.name : ""}</td>
                    <td className="text">
                      {entry.validFrom && <FormattedDate value={entry.validFrom} />}
                    </td>
                    <td className="text">
                      {entry.validTo && <FormattedDate value={entry.validTo} />}
                    </td>
                    {!disabled && (
                      <td className="jsx" style={{ whiteSpace: "nowrap" }}>
                        <Button
                          type="button"
                          label="Změnit"
                          style={{ color: "white", marginRight: 4, padding: "0px 15px" }}
                          onClick={() => this.startEdit(entry)}
                        />
                        <Button
                          type="button"
                          label="Smazat"
                          style={{ color: "white", padding: "0px 15px" }}
                          onClick={() => this.deleteEntry(entry.id)}
                        />
                      </td>
                    )}
                  </tr>
                ))}
                {entries.length === 0 && (
                  <tr>
                    <td colSpan={disabled ? 3 : 4} className="text" style={{ color: "#888", fontStyle: "italic" }}>
                      Žádné záznamy
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {!disabled && editingEntry !== null && (
          <div style={{ marginTop: 12, padding: "12px", background: "#f5f5f5", borderRadius: 4 }}>
            <strong>{editingEntry === "new" ? "Nový záznam" : "Úprava záznamu"}</strong>
            <div style={{ display: "flex", gap: 12, marginTop: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
              <div>
                <label style={labelStyle}>Veřejná funkce *</label>
                <select
                  value={editForm.esmFunction || ""}
                  onChange={(e) => this.onFormChange("esmFunction", e.target.value)}
                  style={inputStyle}
                >
                  <option value="">-- vyberte --</option>
                  {esmFunctions.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Od data *</label>
                <input
                  type="date"
                  value={editForm.validFrom || ""}
                  onChange={(e) => this.onFormChange("validFrom", e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Do data</label>
                <input
                  type="date"
                  value={editForm.validTo || ""}
                  onChange={(e) => this.onFormChange("validTo", e.target.value)}
                  style={inputStyle}
                />
              </div>
            </div>
            {saveError && <div style={{ color: "red", marginTop: 6 }}>{saveError}</div>}
            <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
              <Button
                type="button"
                label="Uložit"
                style={{ color: "white", padding: "0px 15px" }}
                onClick={this.saveEntry}
              />
              <Button
                type="button"
                label="Zrušit"
                style={{ color: "white", padding: "0px 15px" }}
                onClick={this.cancelEdit}
              />
            </div>
          </div>
        )}

        {!disabled && editingEntry === null && (
          <div style={{ marginTop: 8 }}>
            <Button
              type="button"
              label="Přidat záznam"
              style={{ color: "white", marginRight: 10, padding: "0px 15px" }}
              onClick={this.startAdd}
            />
          </div>
        )}
      </div>
    );
  }
}

const labelStyle = {
  display: "block",
  fontSize: 12,
  marginBottom: 2,
  color: "#555",
};

const inputStyle = {
  padding: "5px 8px",
  fontSize: 13,
  border: "1px solid #ccc",
  borderRadius: 3,
  minWidth: 160,
};

export default EsmEntriesSection;
